import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Card, Pill } from "../../components/common/Primitives";
import AdminApprovalBadge from "../../components/common/AdminApprovalBadge";
import SandboxKycPanel from "../../components/kyc/SandboxKycPanel";
import KycCompanyDetailsForm from "../../components/kyc/KycCompanyDetailsForm";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import {
  ShieldCheck, UserCircle2, Building2, FileCheck2, Info, Upload,
  CheckCircle2, Circle, MessageCircle, Send, IdCard,
} from "lucide-react";

/**
 * Startup Verification page — splits identity + document proofs into TWO tabs
 * to end the profile/KYC confusion:
 *
 *   Tab A · Individual Verification  — Founder KYC (Aadhaar via DigiLocker,
 *                                       personal PAN, DIN if a director).
 *   Tab B · Business Verification    — CIN, GSTIN, Business PAN, LLPIN,
 *                                       docs, MCA lookup, reviewer thread.
 *
 * Deep-links preserved: `/startup/verification?tab=business&focus=digilocker`
 * still works — the old KYC reminder emails point to `/startup/kyc` which is
 * now redirected here (see App.js).
 */

const CATEGORY_LABEL = {
  pan_card: "PAN Card",
  cin_certificate: "Incorporation / CIN Certificate",
  bank_statement: "Bank Statement (last 12 months)",
  founder_id: "Founders' ID / KYC",
  aadhaar: "Aadhaar",
  gstin: "GSTIN Certificate",
  moa: "MoA / AoA",
};
const humanize = (cat) =>
  CATEGORY_LABEL[cat] ||
  (cat || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const TABS = [
  { id: "individual", label: "Individual Verification", icon: UserCircle2 },
  { id: "business", label: "Business Verification", icon: Building2 },
];

export default function StartupVerification() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "business" ? "business" : "individual";
  const [tab, setTab] = useState(initialTab);
  const [me, setMe] = useState(null);
  const [reqs, setReqs] = useState(null);
  const [thread, setThread] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reply, setReply] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [meRes, reqRes, thRes] = await Promise.all([
      api.get("/startup/me"),
      api.get("/startup/kyc/requirements"),
      api.get("/startup/kyc/thread").catch(() => ({ data: { messages: [] } })),
    ]);
    setMe(meRes.data);
    setReqs(reqRes.data);
    setThread(thRes.data?.messages || []);
  }, []);
  useEffect(() => { refresh().catch(() => {}); }, [refresh]);

  // Deep-link focus support carried over from the old KYC page. `digilocker`
  // stays on the Individual tab; other known focuses live on Business.
  useEffect(() => {
    const focus = searchParams.get("focus");
    if (!focus) return;
    const INDIVIDUAL_FOCUS = new Set(["digilocker", "pan"]);
    const BUSINESS_FOCUS = new Set(["cin", "gstin", "company-pan"]);
    if (INDIVIDUAL_FOCUS.has(focus)) setTab("individual");
    else if (BUSINESS_FOCUS.has(focus)) setTab("business");
    else return; // unknown focus — don't move the user
    const map = {
      digilocker: "sandbox-digilocker-card",
      pan: "sandbox-pan-card",
      cin: "sandbox-cin-card",
      gstin: "sandbox-gstin-card",
      "company-pan": "sandbox-company-pan-card",
    };
    const target = map[focus];
    if (!target) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid='${target}']`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-4", "ring-gold/70", "transition");
      setTimeout(() => el.classList.remove("ring-4", "ring-gold/70", "transition"), 2500);
    }, 900);
    return () => clearTimeout(t);
  }, [searchParams]);

  const submitKyc = async () => {
    setBusy(true);
    try {
      await api.post("/startup/kyc/submit");
      toast.success("KYC submitted for admin review.");
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && detail.error === "kyc_docs_missing") {
        const missing = (detail.missing || []).map(humanize).join(", ");
        toast.error(missing ? `Missing: ${missing}` : "Upload all required documents first.");
      } else {
        toast.error(formatApiError(err));
      }
    } finally { setBusy(false); }
  };

  const switchTab = (id) => {
    setTab(id);
    const p = new URLSearchParams(searchParams);
    if (id === "individual") p.delete("tab"); else p.set("tab", id);
    setSearchParams(p, { replace: true });
  };

  const startup = me?.startup || {};
  const kycStatus = startup.kyc_status || "Not Submitted";
  const founderKycStatus = startup.founder_id_verified ? "Verified" : "Not Verified";
  const kycTone =
    kycStatus === "Approved" ? "success" :
    kycStatus === "Under Review" ? "warn" :
    kycStatus === "More Information Required" ? "warn" : "muted";

  const checklist = reqs?.checklist || [];
  const ready = !!reqs?.ready;
  const isDirector = !!startup.founder_din;

  return (
    <DashboardShell title="Verification" subtitle="STARTUP · TRUST & COMPLIANCE">
      <Toaster richColors position="top-right" />

      {/* Status ribbon — always visible so users don't get lost between tabs */}
      <Card testId="verification-status-ribbon" className="mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <ShieldCheck size={22} className="text-gold shrink-0" />
          <div className="flex-1 min-w-[200px]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              Overall verification status
            </div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <Pill tone={kycTone} data-testid="verification-overall-status">{kycStatus}</Pill>
              <AdminApprovalBadge
                entity={{
                  kyc_status: startup.kyc_status,
                  admin_temp_approved: startup.admin_temp_approved,
                  admin_approved_at: startup.admin_approved_at,
                  admin_approved_by: startup.admin_approved_by,
                  founder_id_verified: startup.founder_id_verified,
                  cin: startup.cin,
                  cin_verified_at: startup.cin_verified_at,
                  gstin: startup.gstin,
                  gstin_verified_at: startup.gstin_verified_at,
                }}
                size="sm"
                testId="verification-admin-approval"
              />
              <span className="text-xs text-ink-muted">·</span>
              <span className="text-xs text-ink-muted flex items-center gap-1">
                <UserCircle2 size={12} /> Founder ID:
                <Pill tone={founderKycStatus === "Verified" ? "success" : "muted"} data-testid="verification-founder-status">
                  {founderKycStatus}
                </Pill>
              </span>
              {startup.cin && (
                <span className="text-xs text-ink-muted flex items-center gap-1">
                  <span>·</span> <Building2 size={12} /> CIN <span className="font-mono">{startup.cin}</span>
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={submitKyc}
            disabled={!ready || busy || kycStatus === "Under Review" || kycStatus === "Approved"}
            data-testid="submit-kyc-btn"
          >
            {busy ? "Submitting…" : ready ? "Submit for KYC Review" : "Complete checklist to submit"}
          </Button>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-navy/10" data-testid="verification-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => switchTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors text-sm ${
                active
                  ? "border-emerald-600 text-emerald-700 font-heading"
                  : "border-transparent text-ink-muted hover:text-ink"
              }`}
              data-testid={`verification-tab-${t.id}`}
            >
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "individual" && (
        <div className="space-y-6" data-testid="verification-individual-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            Founder KYC · Powered by DigiLocker & Income Tax
          </div>
          <div className="text-sm text-ink-soft">
            Verify your identity as the founder / authorised signatory. Aadhaar via DigiLocker + PAN
            are pulled instantly and locked once confirmed. Any changes require admin unlock.
          </div>

          <SandboxKycPanel showCompany={false} />

          {isDirector && (
            <Card testId="verification-din-card">
              <div className="flex items-start gap-3">
                <IdCard size={18} className="text-emerald-600 mt-1 shrink-0" />
                <div>
                  <div className="font-heading text-ink">Director Identification Number</div>
                  <div className="text-xs text-ink-muted mt-0.5">
                    Auto-synced from MCA using your CIN. Held as-of the last MCA lookup.
                  </div>
                  <div className="mt-2 font-mono text-sm text-ink-soft">
                    DIN: <span className="text-ink font-semibold">{startup.founder_din}</span>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <div className="text-xs text-ink-muted italic pt-2">
            Need to update business identity (CIN, GSTIN, PAN)?
            <button
              onClick={() => switchTab("business")}
              className="ml-1 text-emerald-700 hover:underline font-heading"
              data-testid="link-to-business-tab"
            >
              Switch to Business Verification →
            </button>
          </div>
        </div>
      )}

      {tab === "business" && (
        <div className="space-y-6" data-testid="verification-business-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">
            Business Identity · MCA, Income Tax & GSTN
          </div>

          {/* Company identifiers — CIN, GSTIN, Business PAN, LLPIN */}
          <KycCompanyDetailsForm />

          {/* Instant company checks */}
          <SandboxKycPanel showCompany showIndividual={false} />

          {/* Document checklist */}
          <Card testId="business-doc-checklist">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
              <div>
                <div className="overline">Required Documents</div>
                <div className="text-xs text-ink-muted mt-0.5">
                  {checklist.filter((i) => i.uploaded).length} of {checklist.length} uploaded
                </div>
              </div>
              <Link
                to="/startup/documents"
                className="text-xs font-heading text-ink-soft hover:text-gold-soft inline-flex items-center gap-1"
                data-testid="open-vault-link"
              >
                <FileCheck2 size={12} /> Open Document Vault
              </Link>
            </div>
            <ul className="space-y-2 text-sm">
              {checklist.length === 0 && (
                <li className="text-ink-faint text-xs">Checklist loading…</li>
              )}
              {checklist.map((item) => (
                <li
                  key={item.category}
                  className="flex items-center gap-2"
                  data-testid={`kyc-check-${item.category}`}
                >
                  {item.uploaded ? (
                    <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                  ) : (
                    <Circle size={16} className="text-ink-faint shrink-0" />
                  )}
                  <span className={item.uploaded ? "text-ink" : "text-ink-soft"}>
                    {humanize(item.category)}
                  </span>
                  {!item.uploaded && (
                    <Link
                      to="/startup/documents"
                      className="ml-auto text-[11px] font-heading text-gold-soft hover:underline flex items-center gap-1"
                      data-testid={`kyc-upload-${item.category}`}
                    >
                      <Upload size={11} /> Upload
                    </Link>
                  )}
                </li>
              ))}
            </ul>
            <div className="text-xs text-ink-faint flex items-center gap-1 mt-3 pt-3 border-t border-navy/[0.06]">
              <Info size={12} /> Admin can reconfigure required docs from Settings.
            </div>
          </Card>

          {/* Reviewer thread */}
          {(thread.length > 0 || kycStatus === "Under Review" || kycStatus === "More Information Required") && (
            <Card testId="kyc-thread-card-founder">
              <div className="flex items-center gap-2 mb-3">
                <MessageCircle size={16} className="text-gold" />
                <div>
                  <div className="font-heading text-ink text-lg">Reviewer Thread</div>
                  <div className="text-xs text-ink-muted">Reply here — the reviewer sees it in their queue instantly.</div>
                </div>
              </div>
              <div className="space-y-2 mb-3 max-h-[360px] overflow-y-auto">
                {thread.length === 0 && (
                  <div className="text-xs text-ink-muted italic" data-testid="kyc-thread-empty-founder">
                    No messages yet. The reviewer will post here after they review your KYC.
                  </div>
                )}
                {thread.map((m) => {
                  const mine = m.author_role === "founder";
                  return (
                    <div
                      key={m.message_id}
                      className={`p-2.5 rounded border text-sm ${mine
                        ? "bg-navy/[0.03] border-navy/10 text-ink"
                        : "bg-emerald-50 border-emerald-200 text-emerald-900"}`}
                      data-testid={`kyc-thread-msg-${m.message_id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                          {mine ? "You" : "VenturaZ Reviewer"} · {m.kind}
                        </span>
                        <span className="text-[10px] font-mono text-ink-muted">
                          {m.created_at ? formatDateTime(m.created_at) : ""}
                        </span>
                      </div>
                      <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                      {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {m.attachments.map((a) => (
                            <span key={a.doc_id} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white border border-navy/10 text-ink inline-flex items-center gap-1">
                              <FileCheck2 size={10} /> {a.filename}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-end gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Reply to the reviewer — they see it in their queue instantly"
                  rows={2}
                  className="flex-1 text-sm bg-white border border-navy/15 rounded p-2 text-ink focus:border-gold outline-none resize-none"
                  data-testid="kyc-thread-reply-input-founder"
                />
                <label
                  className={`px-3 py-2 rounded border border-navy/15 text-xs font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1 cursor-pointer ${uploadBusy ? "opacity-50 pointer-events-none" : ""}`}
                >
                  <Upload size={12} /> {uploadBusy ? "Uploading…" : "Attach"}
                  <input
                    type="file"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadBusy(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", file);
                        fd.append("category", "founder_reply");
                        const { data } = await api.post("/startup/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setAttachments((a) => [...a, {
                          doc_id: data.doc_id, filename: data.filename || file.name, category: "founder_reply",
                        }]);
                        toast.success("Attached — press Send to include in your reply.");
                      } catch (err) { toast.error(formatApiError(err)); }
                      finally { setUploadBusy(false); e.target.value = ""; }
                    }}
                  />
                </label>
                <button
                  onClick={async () => {
                    if (!reply.trim() && attachments.length === 0) return;
                    try {
                      await api.post("/startup/kyc/reply", {
                        body: reply.trim() || "(attachment only)",
                        attachments: attachments.length ? attachments : undefined,
                      });
                      setReply(""); setAttachments([]);
                      toast.success("Reply sent.");
                      await refresh();
                    } catch (e) { toast.error(formatApiError(e)); }
                  }}
                  disabled={!reply.trim() && attachments.length === 0}
                  className="px-3 py-2 rounded bg-navy text-white text-xs font-heading hover:bg-[#256E58] disabled:opacity-40 inline-flex items-center gap-1"
                  data-testid="kyc-thread-reply-send-founder"
                >
                  <Send size={12} /> Send
                </button>
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {attachments.map((a) => (
                    <span key={a.doc_id} className="text-[11px] font-mono px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 inline-flex items-center gap-1">
                      <FileCheck2 size={11} /> {a.filename}
                      <button
                        onClick={() => setAttachments((x) => x.filter((y) => y.doc_id !== a.doc_id))}
                        className="ml-1 text-emerald-700 hover:text-rose-700"
                      >×</button>
                    </span>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}
    </DashboardShell>
  );
}
